import React, {useEffect} from 'react';
import classNames from 'classnames';
import styles from '@dazn/lp-web/src/components/VideoBanner/video-banner.module.css';
import {useVideoBannerContext} from '@dazn/lp-web/src/components/VideoBanner/VideoBannerContext';
import useVideoControls from '@dazn/lp-web/src/hooks/useVideoBannerControls';
import {deviceDetector} from '@dazn/lp-web/src/utils/deviceDetector';
import {useVideoBannerPlayer} from '@dazn/lp-web/src/components/VideoBanner/useVideoBannerPlayer';
import {VideoProgressbar} from '@dazn/lp-web/src/components/VideoBanner/VideoProgressbar';
import {VideoControls} from '@dazn/lp-web/src/components/VideoBanner/VideoControls';
import {VideoMobileControls} from '@dazn/lp-web/src/components/VideoBanner/VideoMobileControls';
import {CrossIcon} from '../Icons';
import {IVideo} from '../../types';
import {useResponsiveVideo} from './useResponsiveVideo';
import {parseTime} from '../../utils/videoBannerUtils';

interface VideoPlayerProps {
    url: IVideo;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({url}) => {
    const {
        setTotalMilliseconds,
        setCurrentTime,
        setIsPlaying,
        setIsLoaded,
        setTotalDuration,
        setIsFullScreen,
        isLoaded,
        videoRef,
        screenRef,
        thumbnail,
        isHovered,
        isFullScreen,
        currentTime
    } = useVideoBannerContext();

    const {
        handleTimeUpdate,
        toggleFullScreen,
        togglePlayPause,
        handleHover,
        handleBackward10Seconds,
        handleForward10Seconds,
        handleVolumeUp,
        handleVolumeDown,
        toggleMuteUnmute,
        handleCloseModal
    } = useVideoBannerPlayer();

    const {source, imgSource} = useResponsiveVideo({
        portraitSource: url.mobile,
        landscapeSource: url.default,
        portraitImgSource: (thumbnail?.mobile ?? thumbnail?.default) || '',
        landscapeImgSource: thumbnail?.default || ''
    });

    const currentPlayedtime = parseTime(currentTime);
    // Use the custom hook for video controls
    useVideoControls({
        videoRef,
        isLoaded,
        source,
        imgSource,
        currentPlayedtime,
        setCurrentTime,
        setTotalDuration,
        setTotalMilliseconds,
        setIsPlaying,
        toggleFullScreen,
        setIsFullScreen,
        togglePlayPause,
        handleBackward10Seconds,
        handleForward10Seconds,
        handleVolumeUp,
        handleVolumeDown,
        toggleMuteUnmute
    });

    useEffect(() => {
        if (thumbnail && thumbnail?.default) {
            const img = new Image();
            img.src = thumbnail?.default;
            img.onload = () => {};
            img.onerror = () => {
                new Error('Error loading poster image');
            };
        }
    }, [thumbnail?.default]);

    return (
        <div
            onClick={() => handleHover(!isHovered ?? true)}
            onMouseEnter={() => handleHover(true)}
            onMouseLeave={() => handleHover(false)}
            className={classNames(styles.videoPlayerWrapper, {
                [styles.isFullscreen]: isFullScreen
            })}
            ref={screenRef}
        >
            <video
                ref={videoRef}
                className={classNames(styles.customVideo, styles.zIndex)}
                onTimeUpdate={handleTimeUpdate}
                onContextMenu={(e) => e.preventDefault()}
                onLoadedData={() => setIsLoaded(true)}
                controls
                playsInline
                poster={imgSource}
                preload="auto"
                webkit-playsinline="true"
            >
                <source src={source} type="video/mp4" />
                Your browser does not support the video tag.
            </video>
            {deviceDetector.isPhoneOnlyDevice && <VideoMobileControls />}
            <div className={classNames(styles.videoControls, styles.zIndex)}>
                <VideoProgressbar />
                <VideoControls />
            </div>
            <button
                className={classNames(styles.closeButton, styles.zIndex)}
                onClick={handleCloseModal}
            >
                <CrossIcon />
            </button>
        </div>
    );
};

export default VideoPlayer;
